import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { ClassChoice } from './ClericChoiceChooser';
import apiFetch from '../../../utils/apiFetch';
import { PRIMAL_ORDERS, getPrimalOrder } from '../../../shared/rules/primalOrder';
import { ELEMENTAL_FURY_OPTIONS, getElementalFury } from '../../../shared/rules/druid.mjs';
import { getKnownForms } from '../../../shared/rules/beastForm';
import { LANDS, canChangeLand, getLand } from '../../../shared/rules/subclasses/druid/land.mjs';

/** Druid 1: choose Magician or Warden. */
export function PrimalOrderChooser(props) {
  return <ClassChoice {...props} classId="druid" label="Primal Order" stateKey="primalOrder" route="primal-order" bodyKey="orderId" options={PRIMAL_ORDERS} getChoice={getPrimalOrder}/>;
}

/** Druid 7: choose Potent Spellcasting or Primal Strike. */
export function ElementalFuryChooser(props) {
  return <ClassChoice {...props} classId="druid" label="Elemental Fury" stateKey="elementalFury" route="elemental-fury" bodyKey="optionId" options={ELEMENTAL_FURY_OPTIONS} getChoice={getElementalFury}/>;
}

/** Circle of the Land Spells: the land, chosen after each Long Rest. It stays until it is changed. */
export function LandChooser({ form, characterId, onCharacterChange }) {
  const current = getLand(form);
  const [selected, setSelected] = useState(current?.id || 'arid');
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const open = canChangeLand(form);
  const save = async () => {
    setSaving(true); setError('');
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/classes/druid/land`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ landId: selected }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Your land could not be saved.');
      onCharacterChange?.((previous) => ({ ...previous, classState: { ...previous.classState, druid: { ...previous.classState?.druid, land: result.land } } }));
    } catch (failure) { setError(failure.message); } finally { setSaving(false); }
  };
  return <div className="fighting-style-chooser">
    <p className="feature-sheet-hint">{current ? `${current.name} land. ` : ''}{open ? 'Choose a land. You can change it after each Long Rest.' : 'You can change it after your next Long Rest.'}</p>
    {error && <Alert variant="danger">{error}</Alert>}
    <Form.Label htmlFor="druid-land">Land</Form.Label>
    <Form.Select id="druid-land" value={selected} disabled={saving || !open} onChange={(event) => setSelected(event.target.value)}>
      {Object.values(LANDS).map((land) => <option key={land.id} value={land.id}>{land.name}</option>)}
    </Form.Select>
    <div className="fighting-style-actions"><Button size="sm" onClick={save} disabled={saving || !open || selected === current?.id}>{saving ? 'Saving…' : 'Save land'}</Button></div>
  </div>;
}

const crLabel = (value) => (value === 0.25 ? '1/4' : value === 0.5 ? '1/2' : value === 0.125 ? '1/8' : String(value));
const speedLabel = (speed = {}) => Object.entries(speed).filter(([mode]) => mode !== 'walk').map(([mode, feet]) => `${mode[0].toUpperCase()}${mode.slice(1)} ${feet}`).join(', ');

/**
 * Wild Shape (Druid 2): the Beast forms known. The server checks the level's limits and the swap after a Long Rest.
 * In the level-up window (`forNextLevel`) it offers the coming level's Beasts, keeps the saved forms, and hands the picks
 * to `onPicksChange` (with whether there are enough) and starts from `initialPicks`; the window saves them after the level.
 */
export function KnownFormsChooser({ form, characterId, onCharacterChange, forNextLevel = false, initialPicks = null, onPicksChange }) {
  const [catalog, setCatalog] = useState(null), [picks, setPicks] = useState(initialPicks || getKnownForms(form));
  const [search, setSearch] = useState(''), [saving, setSaving] = useState(false), [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/druid/beast-forms${forNextLevel ? '?level=next' : ''}`, { cache: 'no-store' });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.message || 'The Beasts could not be loaded.');
        if (live) setCatalog(result);
      } catch (failure) { if (live) setError(failure.message); }
    })();
    return () => { live = false; };
  }, [characterId, forNextLevel]);
  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (catalog?.forms || []).filter((beast) => !query || beast.name.toLowerCase().includes(query));
  }, [catalog, search]);
  const limit = catalog?.limit?.forms || 0;
  const saved = getKnownForms(form);
  const unchanged = picks.length === saved.length && picks.every((index) => saved.includes(index));
  const locked = (index) => forNextLevel && saved.includes(index);
  const toggle = (index) => {
    if (locked(index)) return;
    const next = picks.includes(index) ? picks.filter((pick) => pick !== index) : picks.length < limit ? [...picks, index] : picks;
    setPicks(next);
    onPicksChange?.(next, next.length === limit);
  };
  const save = async () => {
    setSaving(true); setError('');
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/druid/known-forms`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ forms: picks }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'Your forms could not be saved.');
      onCharacterChange?.((previous) => ({ ...previous, classState: { ...previous.classState, druid: { ...previous.classState?.druid, knownForms: result.knownForms } } }));
    } catch (failure) { setError(failure.message); } finally { setSaving(false); }
  };
  return <div className="fighting-style-chooser">
    <p className="feature-sheet-hint">{limit ? `${picks.length} of ${limit} forms chosen.` : 'Loading Beasts…'} {forNextLevel ? (saved.length ? 'Your current forms stay; add the new ones.' : '') : saved.length ? (catalog?.canSwap ? 'You can replace one form now.' : 'You can replace one form after a Long Rest.') : ''}</p>
    {error && <Alert variant="danger">{error}</Alert>}
    <Form.Control size="sm" aria-label="Search Beasts" placeholder="Search Beasts…" value={search} onChange={(event) => setSearch(event.target.value)}/>
    {shown.map((beast) => <Form.Check key={beast.index} id={`known-form-${beast.index}`} className="small mt-1" checked={picks.includes(beast.index)} disabled={saving || locked(beast.index) || (!picks.includes(beast.index) && picks.length >= limit)}
      label={`${beast.name} · CR ${crLabel(beast.challengeRating)} · AC ${beast.armorClass} · Speed ${beast.speed.walk ?? 0}${speedLabel(beast.speed) ? `, ${speedLabel(beast.speed)}` : ''}`} onChange={() => toggle(beast.index)}/>)}
    {!forNextLevel && <div className="fighting-style-actions"><Button size="sm" onClick={save} disabled={saving || picks.length !== limit || unchanged}>{saving ? 'Saving…' : 'Save forms'}</Button></div>}
  </div>;
}
